import type { RefOrGetter } from "ls/common/types";
import type { ShortcutJs } from "@/generated/models";
import { useFileLocation } from "ls/features/files/useFileLocation";
import { useRecents } from "ls/features/recents/useRecents";
import { computed, toValue } from "vue";

interface UseEmbedLinkSuggestedOptions {
  shortcut: RefOrGetter<string>;
  limit?: number;
  sameWorkspaceLimit?: number;
  filterFiles?: (file: ShortcutJs) => boolean;
}

export function useEmbedLinkSuggestions(options: UseEmbedLinkSuggestedOptions) {
  const {
    shortcut,
    limit = Number.POSITIVE_INFINITY,
    sameWorkspaceLimit = -1,
    filterFiles,
  } = options;

  const { workspace } = useFileLocation(shortcut);
  const recents = useRecents();

  const suggestions = computed(() => {
    if (!recents.value) {
      return [];
    }

    const recentsToSearch = filterFiles ? recents.value.filter(r => filterFiles(r.Shortcut)) : recents.value;

    if (!workspace.value || sameWorkspaceLimit < 0) {
      return recentsToSearch.slice(0, Math.min(limit, recentsToSearch.length));
    }

    const workspaceId = workspace.value.id;
    const sameWorkspaceRecents = recentsToSearch.filter(r => r.FilesystemId === workspaceId && r.Shortcut.Shortcut !== toValue(shortcut));
    const anotherWorkspaceRecents = recentsToSearch.filter(r => r.FilesystemId !== workspaceId);
    const sameWorkspaceCount = Math.min(sameWorkspaceLimit, sameWorkspaceRecents.length);
    const anotherWorkspaceCount = Math.min(limit - sameWorkspaceCount, anotherWorkspaceRecents.length);
    return [...sameWorkspaceRecents.slice(0, sameWorkspaceCount), ...anotherWorkspaceRecents.slice(0, anotherWorkspaceCount)];
  });

  return suggestions;
}
